import React from 'react';
import { NEURO_ID_ENABLED, STAGE, APP_PROFILE } from 'env';

type NeuroIdParam = string | number | boolean;

type NeuroIdFunction = (
    action: string,
    subAction?: NeuroIdParam,
    value?: NeuroIdParam,
) => void;

type NeuroIdObjectProps = {
    version: string,
}

type NeuroIdWindow = {
    nid?: NeuroIdFunction & NeuroIdObjectProps,
};

const nidWindow = window as NeuroIdWindow;

const log = (title: string, message: string): void => {
    if (STAGE !== 'production') {
         console.log(`%c${title}: ${message}`, 'color: #bada55');
    }
}

const registerNeuroIdEvent: NeuroIdFunction = (
    action: string,
    subAction?: NeuroIdParam,
    value?: NeuroIdParam,
): void => {
    nidWindow.nid(action, subAction, value);
    log('NEURO-ID CALL', [action, subAction, value].filter(v => v || v === false).join(' '));
}

const useNeuroId = () => {
    const cookieAccepted = localStorage.getItem('cookieAccepted');
    const [nidVersion, setNidVersion] = React.useState(nidWindow.nid ? nidWindow.nid.version : '');
    const [nidVersionInterval, setNidVersionInterval] = React.useState(null);
    const [nidCallQueue, setNidCallQueue] = React.useState<Array<() => void>>([]);

    const start = React.useCallback(() => registerNeuroIdEvent('start'), []);
    const setDebug = React.useCallback((value: boolean) => registerNeuroIdEvent('setDebug', value), []);
    const setUserId = React.useCallback((value: string) => registerNeuroIdEvent('setUserId', value), []);
    const setVariable = React.useCallback((name: string, value?: NeuroIdParam) => registerNeuroIdEvent('setVariable', name, value), []);
    const setCheckpoint = React.useCallback((name: string) => registerNeuroIdEvent('setCheckpoint', name), []);
    const setSubmit = React.useCallback(() => registerNeuroIdEvent('applicationSubmit'), []);

    const enqueueOrMakeCall = React.useCallback((fn: () => void) => {
        if (!NEURO_ID_ENABLED) {
            return;
        }
        if (nidVersion) {
            fn();
            return;
        }
        setNidCallQueue([...nidCallQueue, fn]);
    }, [nidVersion]);

    React.useEffect(() => {
        if (!NEURO_ID_ENABLED) {
            return;
        }
        if (nidVersion) {
            log('INFO', `nid is ready in version [${nidVersion}]`);
            return;
        }

        setNidVersionInterval(setInterval(() => setNidVersion(nidWindow.nid ? nidWindow.nid.version : ''), 100));
    }, []);

    React.useEffect(() => {
        if (!cookieAccepted && (!nidVersion || !nidVersionInterval)) {
            return;
        }

        clearInterval(nidVersionInterval);
        setNidVersionInterval(null);

        log('INFO', `nid was loaded in version [${nidVersion}]`);

        start();
        setDebug(STAGE === 'development');
        setVariable('funnel', APP_PROFILE === 'partners' ? 'parceiro' : 'orgânico');

        nidCallQueue.forEach(fn => fn());
        setNidCallQueue([]);
    }, [nidVersion, cookieAccepted]);

    return {
        setUserId: (value: string) => enqueueOrMakeCall(() => setUserId(value)),
        setVariable: (name: string, value?: NeuroIdParam) => enqueueOrMakeCall(() => setVariable(name, value)),
        setCheckpoint: (name: string) => enqueueOrMakeCall(() => setCheckpoint(name)),
        setSubmit: () => enqueueOrMakeCall(setSubmit),
        start: () => enqueueOrMakeCall(start)
    };
};

export default useNeuroId;
